import React, { useCallback, useEffect, useRef, useState } from "react";
import FieldRenderer from "../../components/Form/FieldRenderer";
import { LoginSchema } from "../../components/Form/Schema";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../utils/context/AppContext";
import { getFetchData, postFetchData } from "../../utils/api/FetchData";
import { ToastNotifier } from "../../components/ui/ToastNotifier";
import { APICONSTANT } from "../../utils/api/Config";
import loginbg from "../../assets/survey-login-bg-img.png";
import { Icon } from "@iconify/react";

import {
  loadCaptchaEnginge,
  LoadCanvasTemplate,
  LoadCanvasTemplateNoReload,
  validateCaptcha,
} from "react-simple-captcha";
import DynamicLoader from "../../components/Form/FormFields/DynamicLoader";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedDistrict,
  setSelectedDistrictCode,
  setSelectedDivision,
  setSelectedDivisionCode,
  setSelectedRegion,
  setSelectedRegionCode,
  setSelectedTaluk,
  setSelectedTalukCode,
  setSelectedVillageCode,
} from "../../components/filter/districtDetailsFilter/store";
import CustomHookForRuralDetails from "../../components/ruralDetailsCount/CustomHookForRuralDetails";
import CustomHookForFilter from "../../components/filter/districtDetailsFilter/CustomHookForFilter";
import { setSessionItem } from "../../utils/cookie/Cookie";
import JSEncrypt from "jsencrypt";

function SignIn() {
  const dispatch = useDispatch();
  const { DistrictDetailsHandleChange, details } = CustomHookForFilter();

  const selectorData = useSelector((state) => state?.dynamicData);
  const isLoading = selectorData.loaders[APICONSTANT.login];

  const districtDetails = useSelector((state: RootState) => state.district);

  const {
    register,
    formState: { errors },
    setValue,
    setError,
    watch,
    handleSubmit,
  } = useForm<FormData>({
    mode: "all",
    resolver: yupResolver(LoginSchema),
  });

  const [svg, setSvg] = useState(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const sessionId = useRef<string | null>(null);

  // update value

  const navigate = useNavigate();
  const { signIn } = useAppContext();

  const formConfig = [
    {
      name: "username",
      type: "text",
      placeholder: "Enter your email",
      category: "input",
    },
    {
      type: "password",
      name: "password",
      placeholder: "Enter your password",
      category: "input",
    },
    {
      type: "text",
      name: "captcha",
      placeholder: "Enter your Capcha",
      category: "input",
    },
  ];

  const onSubmit = useCallback(
    async (data) => {
      // CheckCapcha(data)
      await submitData(data);
    },
    [navigate],
  );

  const submitData = async (data) => {
    try {
      // 1. Fetch Public Key from Backend
      const publicKeyRes = await getFetchData(
        0,
        dispatch,
        APICONSTANT.getPublicKey,
      );
      if (!publicKeyRes || !publicKeyRes.data?.publicKey) {
        ToastNotifier("Failed to get security key. Please try again.", "error");
        return;
      }

      const publicKey = publicKeyRes.data.publicKey;

      // 2. Encrypt Data using RSA Public Key
      const crypt = new JSEncrypt();
      crypt.setPublicKey(publicKey);

      const payload = { sessionId: sessionId.current, ...data };
      const encryptedData = crypt.encrypt(JSON.stringify(payload));

      if (!encryptedData) {
        ToastNotifier("Encryption failed. Please try again.", "error");
        return;
      }

      // 3. Post Encrypted Data
      const loginRes = await postFetchData(0, dispatch, APICONSTANT.login, {
        enc: encryptedData,
      });
      if (loginRes?.status == 1) {
        ToastNotifier(loginRes.message, "success");

        const loginResData = loginRes?.data;
        await setSessionItem("role_id", loginResData?.role_id);
        await setSessionItem("username", loginResData?.username);

        await signIn({ key: "token", value: loginResData?.token });

        await details(
          APICONSTANT.region_list,
          [],
          setSelectedRegion,
          // setSelectedRegionCode
        );

        if (loginResData?.region_code) {
          dispatch(setSelectedRegionCode([loginResData?.region_code]));
          // dispatch(setSelectedTalukCode(["01"]));

          await details(
            APICONSTANT.region_list,
            [],
            setSelectedRegion,
            setSelectedRegionCode,
          )
            .then(() => {
              if (loginResData?.district_code) {
                DistrictDetailsHandleChange(
                  "region_code",
                  loginResData?.region_code,
                  APICONSTANT.district_list,
                  setSelectedDistrict,
                );

                dispatch(
                  setSelectedDistrictCode([loginResData?.district_code]),
                );
              }
            })
            .then(async () => {
              if (loginResData?.division_code) {
                DistrictDetailsHandleChange(
                  "district_code",
                  loginResData?.district_code,
                  APICONSTANT.division_list,
                  setSelectedDivision,
                );
              }
            })
            .then(async () => {
              if (loginResData?.taluk_code) {
                dispatch(setSelectedTalukCode([loginResData?.taluk_code]));

                DistrictDetailsHandleChange(
                  "taluk_code",
                  loginResData?.taluk_code,
                  APICONSTANT.taluk_list,
                  setSelectedTaluk,
                );
              }
            });

          if (loginResData?.first_login == "first_login") {
            navigate("/change-password");
          } else {
            navigate("/chart/12", { replace: true });
          }
        }
      }
    } catch (error) {
      console.error("Login Error:", error);
      ToastNotifier("An unexpected error occurred. Please try again.", "error");
    }
  };

  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const getcapchaRes = await getFetchData(
        0,
        dispatch,
        APICONSTANT.generateCaptcha,
      );
      if (getcapchaRes && getcapchaRes.status === 1) {
        sessionId.current = getcapchaRes?.data.sessionId ?? null;
        setSvg(getcapchaRes?.data.svg);
      } else {
        console.warn("No Captcha data found");
      }
    } catch (error) {
      console.error("Failed to load CAPTCHA", error);
      ToastNotifier("Failed to load CAPTCHA. Please try again.", "error");
    } finally {
      setCaptchaLoading(false);
    }
  };
  useEffect(() => {
    loadCaptcha();
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      {isLoading ? (
        <DynamicLoader loading={isLoading} />
      ) : (
        <div className="flex flex-col md:flex-row h-screen w-full bg-white">
          <div
            className="hidden md:flex lg:w-[45%] md:w-1/2 flex-col items-center justify-center p-8 bg-cover bg-center"
            style={{
              backgroundImage: `url(${loginbg})`,
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
            }}
          ></div>

          {/* Right  */}
          <div className="w-full md:w-1/2 bg-white flex items-center justify-center p-4 md:p-8 ">
            <div className="w-full max-w-sm bg-white p-5 text-center">
              <h3 className="text-2xl font-semibold  text-[#475569] text-center mb-4">
                Sign In
              </h3>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {formConfig.map((field, index) => (
                  <FieldRenderer
                    key={index}
                    field={field}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    setError={setError}
                    watch={watch}
                  />
                ))}

                <div className="mb-4 flex flex-col sm:flex-row items-center">
                  {/* <input
                    className={`w-full sm:w-auto flex-1 p-2 border-2 border-[#E2E8F0] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder:text-[#94A3B8E5] placeholder:text-[14px] ${
                      errors.password
                        ? "border-red-300 border focus:border-red-900"
                        : ""
                    }`}
                    placeholder="Enter Captcha"
                    id="user_captcha_input"
                    name="user_captcha_input"
                    type="text"
                  /> */}

                  <div className="w-100">
                    <div className="relative flex items-center gap-3 mt-2">
                      {svg && (
                        <div
                          className="flex-shrink-0"
                          dangerouslySetInnerHTML={{ __html: svg }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={loadCaptcha}
                        className="bg-blue-200 text-gray-500 hover:text-gray-700 p-2 rounded-md flex-shrink-0"
                        disabled={captchaLoading}
                        aria-label="Refresh CAPTCHA"
                      >
                        <Icon icon="mdi:refresh" className="w-5 h-5" />
                      </button>

                      {/* <Input
                    label="Captcha"
                    name="captcha"
                    {...register('captcha')}
                    error={!!errors.captcha}
                    size="lg"
                    className={`border  border-blue-gray-200 focus:border-gray-900 ${errors.captcha ? 'border-red-300 border-2 focus:border-red-900' : ''
                      }`}
                  /> */}
                    </div>
                  </div>

                  {/* <div className="ml-0 sm:ml-2 mt-2 sm:mt-0 flex items-center ">
                    <button type="button" className="ml-2 text-[#475569]">
                      <LoadCanvasTemplate
                        reloadText="&#x21bb;"
                        reloadColor="#475569"
                      />
                    </button>
                  </div> */}
                </div>
                <div className="text-start mb-4">
                  {/* <a href={`/forgot-password`} className="text-[#475569] text-sm">
                {" "}
                Forgot Password ?
              </a> */}
                </div>

                <button
                  type="submit" // ✅ important
                  className="w-[50%] bg-[#006CB7] text-white p-3 rounded-sm hover:bg-[#40596b] transition"
                >
                  Sign In
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignIn;
